import { useState, useEffect } from 'react'
import { HealthTrendChart } from '../components/dashboard/HealthTrendChart'
import { useNavigate } from 'react-router-dom'
import { 
  FolderKanban, Target, Users, DollarSign, AlertTriangle, CheckCircle, Clock, Brain, Activity, ChevronRight
} from 'lucide-react'
import { programsService, Program } from '../services/programs'
import { projectsService, Project } from '../services/projects'
import { portfoliosService } from '../services/portfolios'
import { toast } from 'sonner'
import { getErrorMessage, formatCurrency } from '../utils/apiTransformers'
import { HeroSection } from '../components/HeroSection'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts'
import type { ApiErrorLike } from '../utils/apiError'

export default function ProgramDashboard() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState<Program[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [portfolios, setPortfolios] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [programsData, projectsData, portfoliosData] = await Promise.all([
        programsService.list(),
        projectsService.list(),
        portfoliosService.list()
      ])
      setPrograms(programsData)
      setProjects(projectsData)
      setPortfolios(portfoliosData)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error loading data:', error)
      toast.error(getErrorMessage(error) || 'Failed to load program data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading program dashboard...</p>
        </div>
      </div>
    )
  }

  // Calculate aggregate statistics
  const activePrograms = programs.filter(p => p.status === 'active').length
  const totalBudget = programs.reduce((sum, p) => {
    const programProjects = projects.filter(proj => proj.program_id === p.id)
    const programBudget = programProjects.reduce((s, proj) => s + (parseFloat(proj.budget || '0')), 0)
    return sum + programBudget
  }, 0)
  const activeProjects = projects.filter(p => p.status === 'active').length
  const totalProjects = projects.length

  // Calculate resource utilization (simplified - using active projects / total projects)
  const resourceUtilization = totalProjects > 0 ? Math.round((activeProjects / totalProjects) * 100) : 0

  // Program Status Distribution
  const statusData = [
    { 
      name: 'On Track', 
      value: programs.filter(p => p.status === 'active').length, 
      color: '#10b981' 
    },
    { 
      name: 'At Risk', 
      value: programs.filter(p => p.status === 'on_hold').length, 
      color: '#f59e0b' 
    },
    { 
      name: 'Critical', 
      value: programs.filter(p => p.status === 'completed' && projects.filter(proj => proj.program_id === p.id && proj.status === 'active').length > 0).length, 
      color: '#ef4444' 
    },
  ]


  // Budget by Program
  const budgetData = programs.slice(0, 6).map(program => {
    const programProjects = projects.filter(p => p.program_id === program.id)
    const budget = programProjects.reduce((sum, p) => sum + (parseFloat(p.budget || '0')), 0)
    const spent = programProjects.reduce((sum, p) => sum + (parseFloat(p.actual_cost || '0')), 0)
    return {
      program: program.name.length > 20 ? program.name.substring(0, 20) + '...' : program.name,
      budget: budget / 1000000, // Convert to millions
      spent: spent / 1000000,
      variance: (spent - budget) / 1000000,
    }
  }).filter(item => item.budget > 0)

  // Top Performing Programs
  const topPrograms = programs
    .map(program => {
      const programProjects = projects.filter(p => p.program_id === program.id)
      const budget = programProjects.reduce((sum, p) => sum + (parseFloat(p.budget || '0')), 0)
      const spent = programProjects.reduce((sum, p) => sum + (parseFloat(p.actual_cost || '0')), 0)
      const completion = programProjects.length > 0 
        ? Math.round(programProjects.reduce((sum, p) => sum + p.progress, 0) / programProjects.length)
        : 0
      
      // Sprint 11 — replaced Math.random()-based healthScore with a
      // deterministic composite over real signals: completion rate,
      // budget on-or-under-plan, ratio of active vs delayed projects.
      // Anything from 0-100 maps to the same color buckets as before.
      const delayedCount = programProjects.filter(p => {
        if (!p.end_date) return false
        return new Date(p.end_date) < new Date() && p.status !== 'completed'
      }).length
      const onTimeRatio = programProjects.length
        ? 1 - delayedCount / programProjects.length
        : 1
      const budgetHealth = budget > 0 ? Math.min(1, budget / Math.max(spent, 1)) : 1
      const healthScore = Math.max(
        0,
        Math.min(100, Math.round((completion * 0.4) + (onTimeRatio * 100 * 0.35) + (budgetHealth * 100 * 0.25))),
      )

      const portfolio = portfolios.find(p => p.id === program.portfolio_id)

      return {
        id: program.id,
        name: program.name,
        portfolio: portfolio?.name || 'Unassigned',
        health: healthScore,
        projects: programProjects.length,
        completion,
        budget,
        spent,
        // Sprint 11 — was `Math.round(completion * 1.2)` labeled mock.
        // Benefits realization is a real PMO metric backed by the
        // /programs/{id}/benefits/summary endpoint but fetching for every
        // program-card in a list would 1+N. Until we add a bulk endpoint,
        // surface completion as the proxy and label it accurately.
        benefits: completion,
      }
    })
    .filter(p => p.projects > 0)
    .sort((a, b) => b.health - a.health)
    .slice(0, 3)

  // Programs Requiring Attention
  const attentionPrograms = programs
    .map(program => {
      const programProjects = projects.filter(p => p.program_id === program.id)
      const budget = programProjects.reduce((sum, p) => sum + (parseFloat(p.budget || '0')), 0)
      const spent = programProjects.reduce((sum, p) => sum + (parseFloat(p.actual_cost || '0')), 0)
      const overrun = spent > budget ? spent - budget : 0
      const delayedProjects = programProjects.filter(p => {
        if (!p.end_date) return false
        const endDate = new Date(p.end_date)
        return endDate < new Date() && p.status !== 'completed'
      }).length
      
      const portfolio = portfolios.find(p => p.id === program.portfolio_id)
      
      let issue = ''
      let severity: 'high' | 'medium' | 'low' = 'low'
      
      if (overrun > 100000) {
        issue = `Budget overrun by ${formatCurrency(overrun)}`
        severity = 'high'
      } else if (delayedProjects > 2) {
        issue = `${delayedProjects} delayed projects`
        severity = 'high'
      } else if (delayedProjects > 0) {
        issue = `Schedule delay detected`
        severity = 'medium'
      } else {
        return null
      }
      
      // Sprint 11 — real daysOpen from program.created_at, not
      // Math.random(). On unset created_at (legacy rows) fall back to 0.
      const daysOpen = program.created_at
        ? Math.max(0, Math.floor((Date.now() - new Date(program.created_at).getTime()) / 86_400_000))
        : 0

      return {
        id: program.id,
        name: program.name,
        issue,
        severity,
        portfolio: portfolio?.name || 'Unassigned',
        owner: program.owner_id ? `User ${program.owner_id}` : 'Unassigned',
        daysOpen,
      }
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)
    .slice(0, 3)

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-700 border-green-200'
      default: return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  // Program metrics for HeroSection
  const programMetrics = [
    { 
      label: 'Active Programs', 
      value: activePrograms.toString(), 
      subtitle: `+${programs.length - activePrograms} others`,
      icon: FolderKanban 
    },
    { 
      label: 'Total Budget', 
      value: formatCurrency(totalBudget), 
      subtitle: 'Across all programs',
      icon: DollarSign 
    },
    { 
      label: 'Active Projects', 
      value: activeProjects.toString(), 
      subtitle: `of ${totalProjects} total`,
      icon: Target 
    },
    { 
      label: 'Resource Utilization', 
      value: `${resourceUtilization}%`, 
      subtitle: 'Active allocation',
      icon: Users 
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-8">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Hero Section */}
        <HeroSection
          title="Program Dashboard"
          description="Comprehensive overview of all programs with real-time performance metrics"
          variant="level1"
          metrics={programMetrics.map(m => ({
            label: m.label,
            value: m.value,
            subtitle: m.subtitle,
            icon: m.icon
          }))}
        />

        {/* AI Insights Banner — Sprint 14: buttons navigate. Headline
            copy no longer makes a confident "trending ahead of schedule"
            claim about whatever happens to be the top-ranked program —
            that wasn't grounded in any real schedule signal. We say
            something we can actually defend: the program with the best
            composite health-score is named, with no fabricated narrative. */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">AI Program Intelligence</h3>
              <p className="text-purple-100 mb-3">
                {topPrograms.length > 0
                  ? `Top-ranked program by composite health score: ${topPrograms[0]?.name} (${topPrograms[0]?.health}/100). Open recommendations to see the next-best moves.`
                  : 'No programs ranked yet. Add programs and projects to surface recommendations here.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/ai')}
                  className="px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm"
                >
                  View Recommendations
                </button>
                <button
                  onClick={() => navigate('/program')}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors text-sm"
                >
                  Browse Programs
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Program Status Distribution */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Program Status Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {statusData.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-gray-700">{item.name}</span>
                  </div>
                  <span className="font-medium text-gray-900">{item.value} programs</span>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Trends */}
          <HealthTrendChart className="col-span-2" title="Delivery performance over time"
            series={['portfolio_health', 'schedule_stability', 'delivery_confidence']} />
        </div>

        {/* Budget Performance by Program */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Budget Performance by Program</h3>
            <span className="text-xs text-muted-foreground">Total budget vs spent to date</span>
          </div>
          {budgetData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={budgetData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="program" angle={-45} textAnchor="end" height={100} />
                <YAxis label={{ value: 'Budget ($M)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="budget" fill="#3b82f6" name="Allocated Budget ($M)" />
                <Bar dataKey="spent" fill="#10b981" name="Spent ($M)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No budget data available for programs yet.</p>
            </div>
          )}
        </div>

        {/* Top Performing Programs & Attention Required */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Programs */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Programs</h3>
            {topPrograms.length > 0 ? (
              <div className="space-y-4">
                {topPrograms.map((program) => (
                  <div 
                    key={program.id} 
                    onClick={() => navigate(`/program/${program.id}`)}
                    className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 cursor-pointer hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{program.name}</h4>
                        <div className="text-sm text-gray-600 mt-1">Portfolio: {program.portfolio}</div>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {program.projects} projects
                          </span>
                          <span className="flex items-center gap-1">
                            <Activity className="w-4 h-4" />
                            {program.completion}% complete
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Health</div>
                        <div className="text-2xl font-bold text-green-600">{program.health}%</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-green-200">
                      <div>
                        <div className="text-xs text-gray-600">Benefits Realization</div>
                        <div className="text-sm font-medium text-green-700">{program.benefits}%</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600">Budget Status</div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(program.spent)} / {formatCurrency(program.budget)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No programs with projects yet.</p>
              </div>
            )}
          </div>

          {/* Programs Requiring Attention */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Programs Requiring Attention</h3>
              {attentionPrograms.length > 0 && (
                <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  {attentionPrograms.length} Issues
                </span>
              )}
            </div>
            {attentionPrograms.length > 0 ? (
              <div className="space-y-3">
                {attentionPrograms.map((program) => (
                  <div 
                    key={program.id}
                    onClick={() => navigate(`/program/${program.id}`)}
                    className="p-4 border-2 border-red-200 bg-red-50 rounded-lg cursor-pointer hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">{program.name}</div>
                        <div className="text-sm text-gray-600 mb-2">{program.issue}</div>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span>Portfolio: {program.portfolio}</span>
                          <span>Owner: {program.owner}</span>
                        </div>
                      </div>
                      <span className={`px-2.5 py-1 text-xs rounded border ${getSeverityColor(program.severity)}`}>
                        {program.severity}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-red-200">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {program.daysOpen} days open
                      </span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/program/${program.id}`)
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 ml-auto"
                      >
                        Take Action
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No programs requiring immediate attention. All programs are on track.</p>
              </div>
            )}
            <button 
              onClick={() => navigate('/program')}
              className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              View All Programs
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/program')}
              className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left group"
            >
              <FolderKanban className="w-6 h-6 text-gray-600 group-hover:text-purple-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">View All Programs</div>
            </button>
            <button 
              onClick={() => navigate('/program/create')}
              className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left group"
            >
              <Target className="w-6 h-6 text-gray-600 group-hover:text-purple-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Create New Program</div>
            </button>
            <button 
              onClick={() => navigate('/risks')}
              className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left group"
            >
              <AlertTriangle className="w-6 h-6 text-gray-600 group-hover:text-purple-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Risk Dashboard</div>
            </button>
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left group"
            >
              <Users className="w-6 h-6 text-gray-600 group-hover:text-purple-600 mb-2" />
              <div className="font-medium text-gray-900 text-sm">Resource Planning</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
